import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import PageMeta from "../../components/seo/PageMeta";
import { LOCAL_BUSINESS_SCHEMAS } from "../../components/seo/schemas";
import SMSRecruiterCard, { SMS_COPY, INLINE_CTAS } from "../../components/SMSRecruiterCard";
import SMSConsentBlock from "../../components/SMSConsentBlock";
import {
  Mail, Phone, MapPin, Send, Building2, Users, Sparkles, CheckCircle2,
  Clock, ShieldCheck, Stethoscope, Award, ArrowRight, ArrowUpRight,
  MessageSquare, Briefcase, FileText, Calendar, Globe2, Headphones,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------- Catalogs ---------------- */

const EMPLOYER_CATEGORIES = [
  { v: "PRN Shift Coverage",                     source: "employers" },
  { v: "Travel / Contract Staffing",             source: "employers" },
  { v: "Direct Hire / Permanent Placement",      source: "employers" },
  { v: "MSP / VMS Program",                      source: "employers" },
  { v: "Compliance & Credentialing Packet",      source: "employers" },
  { v: "Supplier Diversity (Procurement)",       source: "employers" },
  { v: "RFP / Bid Submission",                   source: "employers" },
  { v: "General Partnership",                    source: "employers" },
];

const CLINICIAN_CATEGORIES = [
  { v: "PRN / Per-Diem Shifts",                  source: "clinicians" },
  { v: "Travel Assignments",                     source: "clinicians" },
  { v: "Local / Permanent Roles",                source: "clinicians" },
  { v: "Allied Health (PT, OT, RT, Lab, Rad)",   source: "clinicians" },
  { v: "License / Compact State Help",           source: "clinicians" },
  { v: "Credentialing & Document Upload",        source: "clinicians" },
  { v: "Pay / Stipend / Benefits Question",      source: "clinicians" },
  { v: "General Question",                       source: "clinicians" },
];

const PROFESSIONS = ["Registered Nurse (RN)", "Licensed Practical Nurse (LPN)", "Nurse Practitioner",
                     "Physical Therapist (PT)", "Occupational Therapist (OT)", "Respiratory Therapist (RT)",
                     "Speech-Language Pathologist (SLP)", "Lab Technologist", "Radiologic Technologist",
                     "Surgical Tech", "Medical Assistant", "Other Allied Health"];

const FACILITY_TYPES = ["Acute-care hospital", "Health system / IDN", "Skilled nursing facility (SNF)",
                        "Long-term acute care (LTAC)", "Outpatient / Ambulatory", "Home health", "FQHC",
                        "Behavioral health", "Government / VA", "MSP / VMS", "Other"];

const URGENCY = ["Today (urgent fill)", "This week", "Within 2 weeks", "Within 30 days", "Exploring / no specific timeline"];

const AVAILABILITY = ["Immediately", "Within 2 weeks", "Within 30 days", "Within 60 days", "Exploring / passive"];

const TYPE_TABS = [
  { k: "employer",  label: "Employer / Facility", Icon: Building2,  testId: "tab-employer" },
  { k: "clinician", label: "Clinician",           Icon: Stethoscope, testId: "tab-clinician" },
  { k: "general",   label: "General",             Icon: MessageSquare, testId: "tab-general" },
];

const CONTACT_OPTIONS = [
  { Icon: Phone,        label: "Call us",  v: "+1 (832) 303-6622", sub: "24/7 PRN line · USA + India", href: "tel:+18323036622", tone: "navy", testId: "option-phone" },
  { Icon: Mail,         label: "Email",    v: "info@tritalcare.com", sub: "We respond inside 1 business day", href: "mailto:info@tritalcare.com", tone: "red",  testId: "option-email" },
  { Icon: Briefcase,    label: "Procurement", v: "partners@tritalcare.com", sub: "Capability packet · W-9 · COI on demand", href: "mailto:partners@tritalcare.com", tone: "navy", testId: "option-procurement" },
  { Icon: Headphones,   label: "Recruiter line", v: "+1 (832) 303-6622", sub: "Mon–Sun · USA + India", href: "tel:+18323036622", tone: "red", testId: "option-recruiter" },
];

const RESPONSE_TIERS = [
  { Icon: Stethoscope, t: "Clinician enquiries", d: "Clinical recruiter response within 1 business day. No auto-dialer call-blitz." },
  { Icon: Building2,   t: "Facility / MSP requests", d: "Account executive response within 1 business day with scope + timeline intake." },
  { Icon: Award,       t: "Procurement / supplier diversity", d: "Capability packet + W-9 + certification letters on demand. 24-hour SLA." },
  { Icon: ShieldCheck, t: "Compliance packet", d: "Audit-ready documentation package on request. Shared under NDA within 2 business days." },
];

const OFFICES = [
  { label: "USA — Head Office",     tag: "Primary",    lines: ["5423 Pecan Fall PL", "Missouri City, TX 77459, USA"] },
  { label: "USA — Corporate Office",tag: "Operations", lines: ["11104 West Airport Blvd, Suite 213", "Stafford, TX 77477, USA"] },
  { label: "India — Operations",    tag: "Global",     lines: ["First Floor, A-83, A Block", "Sector 63, Noida, UP 201309, India"] },
];

const HOURS = [
  { Icon: Phone,    t: "PRN urgent fills", d: "24 / 7 — phone + on-call recruiter dispatch" },
  { Icon: Building2,t: "Account & enterprise", d: "Mon–Fri · 7:00 a.m.–7:00 p.m. CT" },
  { Icon: Globe2,   t: "Global recruiter coverage", d: "Continuous follow-the-sun · USA + India" },
  { Icon: Calendar, t: "Best response window", d: "Submitted before 3 p.m. CT → answered same business day" },
];

/* ---------------- Helpers ---------------- */

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-300 bg-white focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none text-[14px] transition";

function L({ htmlFor, children, required }) {
  return (
    <label htmlFor={htmlFor} className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-600">
      {children}{required && <span className="text-trital-red ml-0.5">*</span>}
    </label>
  );
}

function Field({ label, htmlFor, span, required, children, hint }) {
  return (
    <div className={span ? "md:col-span-2" : ""}>
      <L htmlFor={htmlFor} required={required}>{label}</L>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="mt-1 text-[10.5px] text-slate-400">{hint}</div>}
    </div>
  );
}

/* ---------------- Page ---------------- */

export default function Contact() {
  const location = useLocation();
  const navigate = useNavigate();

  // Tab is driven by URL hash so links can prefill the right form
  const initialTab = useMemo(() => {
    const h = (location.hash || "").replace("#", "").toLowerCase();
    return ["employer", "clinician", "general"].includes(h) ? h : "general";
  }, [location.hash]);
  const [tab, setTab] = useState(initialTab);

  // Keep URL fragment in sync (silently, replaceState)
  useEffect(() => {
    const desired = `#${tab}`;
    if (window.location.hash !== desired) {
      window.history.replaceState(null, "", desired);
    }
  }, [tab]);

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <PageMeta
        title="Contact TRITAL Care® — Talk to our team"
        description="Talk to TRITAL Care® (operating brand of TOPTAL Care Inc.). Phone +1 (832) 303-6622 · Email info@tritalcare.com · USA offices in Missouri City and Stafford, Texas, plus India operations in Noida. One business day response — guaranteed."
        canonical="https://www.tritalcare.com/contact"
        schema={LOCAL_BUSINESS_SCHEMAS}
      />
      <PublicNav />

      {/* 1 · HERO */}
      <section className="relative overflow-hidden bg-trital-mesh" data-testid="contact-hero">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-16 lg:pt-20 lg:pb-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
                <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
                <span className="overline text-trital-navy">Contact TRITAL Care®</span>
              </div>
              <h1 className="mt-6 font-display font-black text-5xl lg:text-[60px] leading-[1.03] tracking-tight text-slate-900 text-balance">
                One message.
                <span className="block text-trital-navy">One business day.</span>
                <span className="block text-trital-red">Zero phone-tree theatrics.</span>
              </h1>
              <p className="mt-5 text-slate-600 max-w-xl leading-relaxed text-lg">
                Tell us what you're trying to solve — a PRN coverage gap, a travel contract, a procurement RFP, or a clinician licensing question — and a human responds inside 24 hours with a practical assessment, not a sales script.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-2">
                <a href="tel:+18323036622" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep" data-testid="hero-call">
                  <Phone className="w-4 h-4" strokeWidth={1.8} /> +1 (832) 303-6622
                </a>
                <a href="mailto:info@tritalcare.com" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-slate-300 text-trital-navy font-semibold hover:bg-slate-50" data-testid="hero-email">
                  <Mail className="w-4 h-4" strokeWidth={1.8} /> info@tritalcare.com
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <motion.div initial={{opacity:0, scale:0.97}} animate={{opacity:1, scale:1}} transition={{duration:0.5}} className="rounded-3xl border border-slate-200 bg-white shadow-navy p-6 relative overflow-hidden" data-testid="hero-card">
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-trital-medical-blue-soft blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="overline text-trital-red">Quick jump</div>
                  <div className="mt-3 grid grid-cols-1 gap-2.5">
                    {TYPE_TABS.map(({ k, label, Icon }) => (
                      <button
                        key={k}
                        onClick={() => { setTab(k); document.getElementById("smart-form")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                        className="group flex items-center gap-3 px-3.5 py-3 rounded-xl bg-slate-50 hover:bg-trital-medical-blue-soft border border-slate-200 hover:border-trital-navy/40 transition-colors text-left"
                        data-testid={`hero-jump-${k}`}
                      >
                        <div className="w-10 h-10 rounded-lg bg-trital-navy text-white flex items-center justify-center"><Icon className="w-5 h-5" strokeWidth={1.8}/></div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-bold text-[14px] text-slate-900">{label}</div>
                          <div className="text-[11px] text-slate-500">Jump to {label.toLowerCase()} form</div>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-trital-red shrink-0" strokeWidth={1.8} />
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 2 · CONTACT OPTIONS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20" data-testid="contact-options">
        <div className="max-w-3xl">
          <div className="overline text-trital-red">Contact options</div>
          <h2 className="mt-3 font-display font-black text-3xl lg:text-[40px] tracking-tight text-slate-900 text-balance">
            Pick the channel that matches your urgency.
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl leading-relaxed">
            For coverage gaps starting today, call. For procurement packets and considered enquiries, email. The form below routes to the right team automatically.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {CONTACT_OPTIONS.map(({ Icon, label, v, sub, href, tone, testId }, i) => {
            const bg = tone === "red" ? "bg-red-50 text-trital-red" : "bg-trital-medical-blue-soft text-trital-navy";
            return (
              <motion.a
                key={label}
                href={href}
                initial={{opacity:0,y:8}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-60px"}}
                transition={{duration:0.3, delay:i*0.04}}
                className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all flex flex-col"
                data-testid={testId}
              >
                <div className={`w-11 h-11 rounded-xl ${bg} flex items-center justify-center`}>
                  <Icon className="w-5 h-5" strokeWidth={1.8}/>
                </div>
                <div className="mt-4 overline text-slate-500">{label}</div>
                <div className="mt-1 font-display font-bold text-[16px] text-slate-900 group-hover:text-trital-navy break-all">{v}</div>
                <div className="mt-1.5 text-[12px] text-slate-500 leading-snug flex-1">{sub}</div>
                <ArrowUpRight className="mt-3 w-4 h-4 text-slate-400 group-hover:text-trital-red transition-colors" strokeWidth={1.8} />
              </motion.a>
            );
          })}
        </div>
      </section>

      {/* 3 · INQUIRY TYPE SELECTOR + 4/5 · SMART FORM */}
      <section id="smart-form" className="bg-slate-50 border-y border-slate-200 scroll-mt-24" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="overline text-trital-red">Send a message</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-[40px] tracking-tight text-slate-900 text-balance">
              Smart form that routes to the right team.
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl leading-relaxed">
              Pick whether you're an employer, a clinician, or none of the above — the form rewrites itself with the questions our team needs to give you a useful first reply.
            </p>
          </div>

          {/* Type selector */}
          <div className="mt-8 inline-flex flex-wrap rounded-full bg-white p-1 border border-slate-200 shadow-card" role="tablist" data-testid="contact-tabs">
            {TYPE_TABS.map(({ k, label, Icon, testId }) => {
              const active = tab === k;
              return (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-controls={`form-${k}`}
                  onClick={() => setTab(k)}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold transition-colors ${
                    active ? "bg-trital-navy text-white shadow" : "text-slate-700 hover:text-trital-navy"
                  }`}
                  data-testid={testId}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.8} /> {label}
                </button>
              );
            })}
          </div>

          {/* Forms */}
          <div className="mt-8 grid lg:grid-cols-[1fr_360px] gap-8 items-start">
            <AnimatePresence mode="wait">
              {tab === "employer"  && <EmployerForm  key="employer" onAfterSubmit={() => navigate("/contact#employer", { replace: true })} />}
              {tab === "clinician" && <ClinicianForm key="clinician" />}
              {tab === "general"   && <GeneralForm   key="general" />}
            </AnimatePresence>
            <SLAaside />
          </div>
        </div>
      </section>

      {/* 6 · BUSINESS CONTACT DETAILS — preceded by SMS recruiter card */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-0" data-testid="contact-sms">
        <SMSRecruiterCard variant="default" headline={SMS_COPY.contact.headline} subtext={SMS_COPY.contact.sub} />
      </section>

      {/* 6 · BUSINESS CONTACT DETAILS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20" data-testid="contact-business-details">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
          <div>
            <div className="overline text-trital-red">Business contact</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-[40px] tracking-tight text-slate-900 leading-tight text-balance">
              Reach the team directly.
            </h2>
            <p className="mt-3 text-slate-600 max-w-md leading-relaxed">
              Use the channels below for capability packets, contract negotiation, NDAs, or anything that doesn't fit a web form.
            </p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            <DetailRow Icon={Phone} eyebrow="Phone — primary" v="+1 (832) 303-6622" sub="24/7 coverage · USA + India" href="tel:+18323036622" testId="detail-phone" />
            <DetailRow Icon={Mail}  eyebrow="Email — general" v="info@tritalcare.com" sub="Routed to the right team within 30 minutes during business hours" href="mailto:info@tritalcare.com" testId="detail-email" />
            <DetailRow Icon={Briefcase} eyebrow="Procurement" v="partners@tritalcare.com" sub="Capability packet · diversity letters · COI · W-9" href="mailto:partners@tritalcare.com" testId="detail-procurement" />
            <DetailRow Icon={FileText}  eyebrow="Compliance" v="compliance@tritalcare.com" sub="Audit packet, primary-source verification samples" href="mailto:compliance@tritalcare.com" testId="detail-compliance" />
          </ul>
        </div>
      </section>

      {/* 7 · OFFICE LOCATIONS */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="contact-offices">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
            <div className="lg:col-span-7">
              <div className="overline text-trital-red">Office locations</div>
              <h2 className="mt-3 font-display font-black text-3xl lg:text-[40px] tracking-tight text-slate-900 text-balance">
                Two Texas anchors, one Noida operations hub.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-slate-600 leading-relaxed">
                Visitor appointments are by prior scheduling only — please email <a className="font-semibold text-trital-navy hover:underline" href="mailto:info@tritalcare.com">info@tritalcare.com</a> first.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {OFFICES.map((o) => (
              <a
                key={o.label}
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.lines.join(", "))}`}
                target="_blank"
                rel="noreferrer"
                className="group rounded-2xl bg-white border border-slate-200 p-6 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all block"
                data-testid={`office-${o.label.toLowerCase().replace(/[^a-z]+/g,'-').replace(/^-|-$/g,'')}`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg bg-trital-navy text-white flex items-center justify-center"><MapPin className="w-5 h-5" strokeWidth={1.8}/></div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{o.tag}</span>
                </div>
                <div className="mt-5 font-display font-bold text-[16px] text-slate-900">{o.label}</div>
                <div className="mt-1.5 text-[13.5px] text-slate-600 leading-snug">
                  {o.lines.map((l) => <div key={l}>{l}</div>)}
                </div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-semibold text-trital-navy group-hover:gap-2 transition-all">
                  Open in Maps <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.8}/>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* 8 · HOURS / RESPONSE EXPECTATIONS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20" data-testid="contact-hours">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="overline text-trital-red">Hours &amp; response expectations</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-[40px] tracking-tight text-slate-900 text-balance">
              When we pick up — and when you'll hear back.
            </h2>
            <p className="mt-4 text-slate-600 max-w-md leading-relaxed">
              We publish response windows so you can plan around them. Our 24/7 PRN line is for facility coverage emergencies; everything else routes through the appropriate desk during business hours.
            </p>
          </div>
          <div className="lg:col-span-7 grid md:grid-cols-2 gap-4">
            {HOURS.map(({ Icon, t, d }, i) => (
              <motion.div
                key={t}
                initial={{opacity:0,y:8}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-60px"}}
                transition={{duration:0.3, delay:i*0.04}}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card flex gap-3"
                data-testid={`hours-${i}`}
              >
                <div className="w-10 h-10 rounded-lg bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5" strokeWidth={1.8}/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-[15px] text-slate-900 leading-tight">{t}</div>
                  <div className="mt-0.5 text-[12.5px] text-slate-600 leading-snug">{d}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Response SLA tier strip */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="contact-sla-tiers">
          {RESPONSE_TIERS.map(({ Icon, t, d }, i) => (
            <div key={t} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 flex gap-2.5" data-testid={`sla-tier-${i}`}>
              <Icon className="w-4 h-4 text-trital-red shrink-0 mt-0.5" strokeWidth={1.8}/>
              <div>
                <div className="font-display font-bold text-[13px] text-slate-900">{t}</div>
                <div className="mt-0.5 text-[11.5px] text-slate-600 leading-snug">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 9 · CTA BAND */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24" data-testid="contact-cta-band">
        <div className="rounded-3xl bg-trital-navy-ink text-white p-8 lg:p-12 relative overflow-hidden shadow-navy">
          <div className="absolute inset-0 bg-trital-navy-mesh opacity-85 pointer-events-none" />
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-trital-red/15 blur-3xl pointer-events-none" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="overline text-white/70">Faster than a form</div>
              <h3 className="mt-3 font-display font-black text-3xl lg:text-[42px] tracking-tight text-white text-balance leading-tight">
                Need an answer right now?
              </h3>
              <p className="mt-3 text-white/75 max-w-xl leading-relaxed">
                Our 24/7 recruiter line is staffed for facility coverage emergencies and urgent clinician inquiries. One call, named exec sponsor, no phone tree.
              </p>
              <div className="mt-5 flex flex-wrap gap-2 text-[12px] text-white/60">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/15">
                  <ShieldCheck className="w-3 h-3 text-trital-red" strokeWidth={1.8}/> NMSDC · HMSDC · NCTRCA · SAM.gov
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/15">
                  <Clock className="w-3 h-3 text-trital-red" strokeWidth={1.8}/> 1-business-day reply guaranteed
                </span>
              </div>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <a href="tel:+18323036622" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-trital-navy font-semibold hover:bg-slate-100" data-testid="cta-call">
                <Phone className="w-4 h-4" strokeWidth={1.8}/> Call +1 (832) 303-6622
              </a>
              <Link to="/capability-statement" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-red text-white font-semibold btn-lift hover:bg-trital-red-deep" data-testid="cta-capability">
                Build capability statement <ArrowRight className="w-4 h-4" strokeWidth={1.8}/>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

/* ---------------- Detail row ---------------- */

function DetailRow({ Icon, eyebrow, v, sub, href, testId }) {
  return (
    <li>
      <a href={href} className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all" data-testid={testId}>
        <div className="w-10 h-10 rounded-lg bg-red-50 text-trital-red flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" strokeWidth={1.8}/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="overline text-slate-500">{eyebrow}</div>
          <div className="mt-1 font-display font-bold text-[15px] text-slate-900 group-hover:text-trital-navy break-all">{v}</div>
          <div className="mt-1 text-[11.5px] text-slate-500 leading-snug">{sub}</div>
        </div>
      </a>
    </li>
  );
}

/* ---------------- SLA aside ---------------- */

function SLAaside() {
  return (
    <aside className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card lg:sticky lg:top-[148px]" data-testid="form-sla-aside">
      <div className="overline text-trital-red">What happens next</div>
      <ol className="mt-3 space-y-3.5">
        {[
          { t: "Routing", d: "Your message lands with the desk best equipped to answer (Clinical, Account, Procurement, Compliance)." },
          { t: "Reply", d: "A human responds inside 1 business day with a practical assessment, not a sales script." },
          { t: "Working session", d: "If we're a fit, we schedule a 20-minute scoping call — never a 45-minute discovery." },
        ].map((s, i) => (
          <li key={s.t} className="flex gap-3">
            <span className="mt-0.5 w-7 h-7 shrink-0 rounded-full bg-trital-navy text-white text-[11px] font-bold flex items-center justify-center">{String(i+1).padStart(2,"0")}</span>
            <div>
              <div className="font-display font-bold text-[14px] text-slate-900">{s.t}</div>
              <div className="text-[12.5px] text-slate-600 leading-snug">{s.d}</div>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-3 flex gap-2.5">
        <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" strokeWidth={1.8}/>
        <div className="text-[11.5px] text-amber-900 leading-snug">
          For coverage emergencies starting <span className="font-bold">today</span>, please call our 24/7 line: <a href="tel:+18323036622" className="font-bold underline">+1 (832) 303-6622</a>.
        </div>
      </div>
    </aside>
  );
}

/* ---------------- Forms ---------------- */

function FormShell({ children, testId, motionKey }) {
  return (
    <motion.form
      key={motionKey}
      initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} exit={{opacity:0, y:-6}}
      transition={{duration:0.3}}
      className="bg-white rounded-3xl border border-slate-200 p-6 lg:p-8 shadow-navy"
      data-testid={testId}
    >
      {children}
    </motion.form>
  );
}

function FormHeader({ Icon, eyebrow, title, sub }) {
  return (
    <>
      <div className="flex items-center gap-2 mb-1 text-[11px] font-bold uppercase tracking-[0.14em] text-trital-red">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.8}/> {eyebrow}
      </div>
      <div className="font-display font-black text-[22px] text-slate-900 leading-tight">{title}</div>
      <p className="mt-1 text-[12.5px] text-slate-500">{sub}</p>
    </>
  );
}

function SuccessBanner({ shown, message }) {
  if (!shown) return null;
  return (
    <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex gap-2.5 items-start" role="status">
      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" strokeWidth={2}/>
      <div>
        <div className="font-bold text-emerald-800 text-[14px]">Message received.</div>
        <div className="text-[12.5px] text-emerald-700">{message}</div>
      </div>
    </div>
  );
}

/* ---------- Employer ---------- */
function EmployerForm() {
  const [f, setF] = useState({
    name: "", email: "", phone: "", company: "", facility_type: FACILITY_TYPES[0],
    role_title: "", region: "", category: EMPLOYER_CATEGORIES[0].v, urgency: URGENCY[2],
    message: "",
  });
  const [smsOptIn, setSmsOptIn] = useState(false); // TCPA: SMS consent
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const requestId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `req-${Date.now()}`;
      const res = await fetch(`/api/request-talent`, {
        method: "POST", headers: { "Content-Type": "application/json", "X-Idempotency-Key": requestId },
        body: JSON.stringify({
          source: "employers",
          name: f.name.trim(), email: f.email.trim(),
          phone: f.phone.trim() || null,
          title: f.role_title || null,
          organization: f.company.trim() || null,
          facilityType: f.facility_type || null,
          location: f.region || null,
          need: f.category,
          timeline: f.urgency,
          sms_opt_in: smsOptIn,
          notes: f.message.trim() || null,
          idempotencyKey: requestId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok !== true || data.email_status !== "sent") throw new Error("Email delivery not confirmed");
      toast.success("Thanks — your inquiry was sent to our team.");
      setDone(true);
      setF({ ...f, name: "", email: "", phone: "", company: "", role_title: "", region: "", message: "" });
      setSmsOptIn(false);
    } catch {
      toast.error("We couldn't deliver that message", { description: "Please try again, or email partners@tritalcare.com." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormShell motionKey="employer" testId="employer-form">
      <FormHeader Icon={Building2} eyebrow="Employer / Facility" title="Tell us how we can help your facility." sub="An account executive will reach out inside 1 business day with a scoped first response — no auto-dialer." />
      <SuccessBanner shown={done} message="An account executive on our team is reviewing your request now. Need anything urgent? Call +1 (832) 303-6622." />
      <div className="mt-5 grid md:grid-cols-2 gap-3" onSubmit={submit}>
        <Field label="Full name" htmlFor="emp-name" required><input id="emp-name" required value={f.name} onChange={upd("name")} className={inputCls} autoComplete="name" data-testid="emp-name"/></Field>
        <Field label="Work email" htmlFor="emp-email" required><input id="emp-email" type="email" required value={f.email} onChange={upd("email")} className={inputCls} autoComplete="email" data-testid="emp-email"/></Field>
        <Field label="Phone (optional)" htmlFor="emp-phone"><input id="emp-phone" type="tel" value={f.phone} onChange={upd("phone")} className={inputCls} autoComplete="tel" data-testid="emp-phone" placeholder="+1 …"/></Field>
        <Field label="Title (optional)" htmlFor="emp-title"><input id="emp-title" value={f.role_title} onChange={upd("role_title")} className={inputCls} data-testid="emp-title" placeholder="e.g. Director of Talent Acquisition"/></Field>
        <Field label="Facility / Health system" htmlFor="emp-company" required span><input id="emp-company" required value={f.company} onChange={upd("company")} className={inputCls} autoComplete="organization" data-testid="emp-company"/></Field>
        <Field label="Facility type" htmlFor="emp-facility-type">
          <select id="emp-facility-type" value={f.facility_type} onChange={upd("facility_type")} className={inputCls} data-testid="emp-facility-type">
            {FACILITY_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Region / market" htmlFor="emp-region" hint="City, state, or 'multi-state'.">
          <input id="emp-region" value={f.region} onChange={upd("region")} className={inputCls} data-testid="emp-region"/>
        </Field>
        <Field label="Inquiry category" htmlFor="emp-category" required span>
          <select id="emp-category" value={f.category} onChange={upd("category")} className={inputCls} data-testid="emp-category">
            {EMPLOYER_CATEGORIES.map((c) => <option key={c.v}>{c.v}</option>)}
          </select>
        </Field>
        <Field label="Urgency" htmlFor="emp-urgency" required>
          <select id="emp-urgency" value={f.urgency} onChange={upd("urgency")} className={inputCls} data-testid="emp-urgency">
            {URGENCY.map((u) => <option key={u}>{u}</option>)}
          </select>
        </Field>
        <Field label="Message" htmlFor="emp-message" required span>
          <textarea id="emp-message" rows={5} required value={f.message} onChange={upd("message")} className={`${inputCls} resize-y`} data-testid="emp-message" placeholder="A sentence or two on scope, timeline, compliance context."/>
        </Field>
      </div>
      
      {/* TCPA: SMS Consent Block — ALWAYS visible (SimpleTexting requirement) */}
      <div className="mt-5">
        <SMSConsentBlock
          checked={smsOptIn}
          onCheckedChange={setSmsOptIn}
          checkboxId="emp-sms-consent"
        />
      </div>
      
      <button onClick={submit} type="submit" disabled={busy} className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep disabled:opacity-70 disabled:cursor-not-allowed" data-testid="emp-submit">
        {busy ? "Sending…" : <>Submit employer inquiry <Send className="w-4 h-4" strokeWidth={1.8}/></>}
      </button>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-[11px] text-slate-500">
        <SMSRecruiterCard variant="inline" headline={INLINE_CTAS.form_employer} testId="emp-text-instead" />
      </div>
    </FormShell>
  );
}

/* ---------- Clinician ---------- */
function ClinicianForm() {
  const [f, setF] = useState({
    name: "", email: "", phone: "", profession: PROFESSIONS[0], specialty: "",
    license_states: "", years: "3–5", availability: AVAILABILITY[1],
    category: CLINICIAN_CATEGORIES[0].v, message: "",
  });
  const [smsOptIn, setSmsOptIn] = useState(false); // TCPA: SMS consent
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const requestId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `req-${Date.now()}`;
      const res = await fetch(`/api/register`, {
        method: "POST", headers: { "Content-Type": "application/json", "X-Idempotency-Key": requestId },
        body: JSON.stringify({
          source: "clinicians",
          name: f.name.trim(), email: f.email.trim(),
          phone: f.phone.trim() || null,
          discipline: f.profession,
          specialty: f.specialty || null,
          licenseState: f.license_states || null,
          yearsExp: f.years,
          assignmentType: f.category,
          shiftPreference: f.availability,
          sms_opt_in: smsOptIn,
          notes: f.message.trim() || null,
          idempotencyKey: requestId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok !== true || data.email_status !== "sent") throw new Error("Email delivery not confirmed");
      toast.success("Thanks — a clinical recruiter will reach out inside 1 business day.");
      setDone(true);
      setF({ ...f, name: "", email: "", phone: "", specialty: "", license_states: "", message: "" });
      setSmsOptIn(false);
    } catch {
      toast.error("We couldn't deliver that message", { description: "Please try again, or email info@tritalcare.com." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormShell motionKey="clinician" testId="clinician-form">
      <FormHeader Icon={Stethoscope} eyebrow="Clinician" title="Tell us about you and what you're looking for." sub="A clinical recruiter will reach out inside 1 business day. No auto-dialers, no application robo-spam." />
      <SuccessBanner shown={done} message="We've routed your message to the right clinical recruiter. Expect an email within 1 business day. Urgent? Call +1 (832) 303-6622." />
      <div className="mt-5 grid md:grid-cols-2 gap-3">
        <Field label="Full name" htmlFor="cli-name" required><input id="cli-name" required value={f.name} onChange={upd("name")} className={inputCls} autoComplete="name" data-testid="cli-name"/></Field>
        <Field label="Email" htmlFor="cli-email" required><input id="cli-email" type="email" required value={f.email} onChange={upd("email")} className={inputCls} autoComplete="email" data-testid="cli-email"/></Field>
        <Field label="Phone (optional)" htmlFor="cli-phone"><input id="cli-phone" type="tel" value={f.phone} onChange={upd("phone")} className={inputCls} autoComplete="tel" data-testid="cli-phone" placeholder="+1 …"/></Field>
        <Field label="Profession" htmlFor="cli-profession" required>
          <select id="cli-profession" value={f.profession} onChange={upd("profession")} className={inputCls} data-testid="cli-profession">
            {PROFESSIONS.map((p) => <option key={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Specialty" htmlFor="cli-specialty" hint="e.g. ICU, ER, Med-Surg, OR, PT outpatient.">
          <input id="cli-specialty" value={f.specialty} onChange={upd("specialty")} className={inputCls} data-testid="cli-specialty"/>
        </Field>
        <Field label="License state(s)" htmlFor="cli-license" hint="Comma-separated. Compact licenses welcome.">
          <input id="cli-license" value={f.license_states} onChange={upd("license_states")} className={inputCls} data-testid="cli-license" placeholder="TX, CA, FL"/>
        </Field>
        <Field label="Years of experience" htmlFor="cli-years">
          <select id="cli-years" value={f.years} onChange={upd("years")} className={inputCls} data-testid="cli-years">
            {["< 1","1–2","3–5","6–10","10+"].map((y) => <option key={y}>{y}</option>)}
          </select>
        </Field>
        <Field label="Availability" htmlFor="cli-availability">
          <select id="cli-availability" value={f.availability} onChange={upd("availability")} className={inputCls} data-testid="cli-availability">
            {AVAILABILITY.map((a) => <option key={a}>{a}</option>)}
          </select>
        </Field>
        <Field label="Inquiry category" htmlFor="cli-category" required span>
          <select id="cli-category" value={f.category} onChange={upd("category")} className={inputCls} data-testid="cli-category">
            {CLINICIAN_CATEGORIES.map((c) => <option key={c.v}>{c.v}</option>)}
          </select>
        </Field>
        <Field label="Message" htmlFor="cli-message" required span>
          <textarea id="cli-message" rows={5} required value={f.message} onChange={upd("message")} className={`${inputCls} resize-y`} data-testid="cli-message" placeholder="What are you hoping to find? Pay range, locations, shift preferences."/>
        </Field>
      </div>
      
      {/* TCPA: SMS Consent Block — ALWAYS visible (SimpleTexting requirement) */}
      <div className="mt-5">
        <SMSConsentBlock
          checked={smsOptIn}
          onCheckedChange={setSmsOptIn}
          checkboxId="cli-sms-consent"
        />
      </div>
      
      <button onClick={submit} type="submit" disabled={busy} className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-red text-white font-semibold btn-lift hover:bg-trital-red-deep disabled:opacity-70 disabled:cursor-not-allowed" data-testid="cli-submit">
        {busy ? "Sending…" : <>Submit clinician inquiry <Send className="w-4 h-4" strokeWidth={1.8}/></>}
      </button>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-[11px] text-slate-500">
        <SMSRecruiterCard variant="inline" headline={INLINE_CTAS.form_clinician} testId="cli-text-instead" />
      </div>
    </FormShell>
  );
}

/* ---------- General ---------- */
function GeneralForm() {
  const [f, setF] = useState({ name: "", email: "", company: "", topic: "General Question", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const TOPICS = ["General Question", "Staffing Request / Client", "Facility Signup", "Partnership / MSP", "PRN Shifts", "Recruiting / Careers", "Privacy Request", "Website Support", "Press / Media", "Other"];

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const requestId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `req-${Date.now()}`;
      const res = await fetch(`/api/contact`, {
        method: "POST", headers: { "Content-Type": "application/json", "X-Idempotency-Key": requestId },
        body: JSON.stringify({
          source: "general",
          name: f.name.trim(), email: f.email.trim(),
          company: f.company.trim() || null,
          subject: f.topic,
          message: f.message.trim() || null,
          idempotencyKey: requestId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok !== true || data.email_status !== "sent") throw new Error("Email delivery not confirmed");
      toast.success("Thanks — your inquiry was sent to our team.");
      setDone(true);
      setF({ name: "", email: "", company: "", topic: "General Question", message: "" });
    } catch {
      toast.error("We couldn't deliver that message", { description: "Please try again, or email info@tritalcare.com." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormShell motionKey="general" testId="general-form">
      <FormHeader Icon={MessageSquare} eyebrow="General Inquiry" title="Anything else — say hi." sub="Press, partnerships, careers, or general questions go here. We respond inside 1 business day." />
      <SuccessBanner shown={done} message="A team member will reply within 1 business day from the email you provided." />
      <div className="mt-5 grid md:grid-cols-2 gap-3">
        <Field label="Full name" htmlFor="gen-name" required><input id="gen-name" required value={f.name} onChange={upd("name")} className={inputCls} autoComplete="name" data-testid="gen-name"/></Field>
        <Field label="Email" htmlFor="gen-email" required><input id="gen-email" type="email" required value={f.email} onChange={upd("email")} className={inputCls} autoComplete="email" data-testid="gen-email"/></Field>
        <Field label="Company / Organization (optional)" htmlFor="gen-company" span><input id="gen-company" value={f.company} onChange={upd("company")} className={inputCls} autoComplete="organization" data-testid="gen-company"/></Field>
        <Field label="Topic" htmlFor="gen-topic" required>
          <select id="gen-topic" value={f.topic} onChange={upd("topic")} className={inputCls} data-testid="gen-topic">
            {TOPICS.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <div className="md:col-span-1" />
        <Field label="Message" htmlFor="gen-message" required span>
          <textarea id="gen-message" rows={5} required value={f.message} onChange={upd("message")} className={`${inputCls} resize-y`} data-testid="gen-message" placeholder="A sentence or two — what's on your mind?"/>
        </Field>
      </div>
      <p className="mt-4 text-xs text-slate-500" aria-live="polite" data-testid="general-contact-routing">
        This inquiry will be routed to {f.topic === "General Question" || f.topic === "Website Support" || f.topic === "Press / Media" || f.topic === "Other" ? "Website Support" : f.topic.includes("Staffing") || f.topic === "Facility Signup" ? "Client Solutions" : f.topic.includes("Partnership") ? "Partnerships" : f.topic.includes("Recruiting") ? "Recruiting" : f.topic.includes("PRN") ? "Shift Operations" : f.topic.includes("Privacy") ? "Privacy" : "Website Support"}.
      </p>
      <button onClick={submit} type="submit" disabled={busy} className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep disabled:opacity-70 disabled:cursor-not-allowed" data-testid="gen-submit">
        {busy ? "Sending…" : <>Send message <Send className="w-4 h-4" strokeWidth={1.8}/></>}
      </button>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-[11px] text-slate-500">
        <span>By submitting you acknowledge our <Link to="/legal/privacy" className="underline hover:text-slate-700">Privacy Notice</Link>.</span>
        <SMSRecruiterCard variant="inline" headline={INLINE_CTAS.form_general} testId="gen-text-instead" />
      </div>
    </FormShell>
  );
}
